import { z } from 'zod';
import { TASK_CATEGORIES } from '../constants.js';
import {
  auditFields,
  isoDateSchema,
  isoDateTimeSchema,
  notesText,
  shortText,
  ulidSchema,
} from '../common.js';

export const taskSchema = z.object({
  type: z.literal('Task'),
  wedId: ulidSchema,
  taskId: ulidSchema,
  title: shortText,
  notes: notesText,
  /**
   * Optional: a task you thought of in the car has no date yet, and forcing one
   * means inventing a deadline, which is how a checklist stops being believed.
   */
  dueDate: isoDateSchema.optional(),
  category: z.enum(TASK_CATEGORIES).default('other'),
  done: z.boolean().default(false),
  doneAt: isoDateTimeSchema.optional(),
  doneBy: z.string().max(64).optional(),
  /** A member's userId. Absent means "either of us", not "nobody". */
  assigneeId: z.string().max(64).optional(),
  /**
   * Set only on tasks the template generated, and stable forever after. It is
   * what makes generating twice safe, and what tells a reschedule which rows it
   * is allowed to move.
   */
  templateKey: z.string().trim().max(60).optional(),
  /**
   * How far before the wedding this template task is anchored. Kept on the row
   * rather than looked up in the template so that editing the template later
   * cannot silently move tasks a couple has already seen.
   */
  daysBefore: z.number().int().min(0).max(1000).optional(),
  ...auditFields,
});
export type Task = z.infer<typeof taskSchema>;

export const createTaskSchema = taskSchema
  .omit({
    type: true,
    wedId: true,
    taskId: true,
    doneAt: true,
    doneBy: true,
    templateKey: true,
    daysBefore: true,
    createdAt: true,
    updatedAt: true,
    updatedBy: true,
  })
  .partial()
  .required({ title: true });
export type CreateTaskInput = z.infer<typeof createTaskSchema>;

export const updateTaskSchema = createTaskSchema.partial();
export type UpdateTaskInput = z.infer<typeof updateTaskSchema>;
